import { useMutation } from '@apollo/client';
import FormContainer from '@components/FormContainer';
import InputDocuments from '@components/InputDocuments';
import { ADD_FILE, ADD_FILE_FOR_TALENT_UPERCUT, DELETE_FILES, DELETE_FILES_FOR_TALENT_UPERCUT } from '@gql/file';
import { Box, Grid } from '@mui/material';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';

type DocumentType = {
    name: string;
    label: string;
    helpText?: string;
    description?: string;
    type: string;
    placeholder?: string;
};

type GroupedDocumentType = {
    id: string;
    name: string;
    description: string;
    documents: DocumentType[];
};

type Props = {
    groupedDocument: GroupedDocumentType;
    documents: Record<string, any[]>;
    talentId?: string;
    onCancel?: () => void;
    onSubmitted?: () => void;
    isEdit?: boolean;
};

const TalentGroupedDocuments = ({
    groupedDocument,
    documents,
    talentId,
    onCancel,
    onSubmitted,
    ...props
}: Props) => {
    const [loading, setLoading] = useState(false);

    // Create default values for form from documents
    const defaultValues = groupedDocument.documents.reduce((acc, doc) => {
        acc[doc.type] = documents[doc.type] || [];
        return acc;
    }, {} as Record<string, any[]>);

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { isDirty },
    } = useForm({
        mode: 'all',
        defaultValues,
    });

    // Create uploaded files state for each document type
    const [uploadedFiles, setUploadedFiles] = useState<Record<string, any[]>>(
        groupedDocument.documents.reduce((acc, doc) => {
            acc[doc.type] = documents[doc.type] || [];
            return acc;
        }, {} as Record<string, any[]>)
    );

    const [deleteFiles] = useMutation(talentId ? DELETE_FILES_FOR_TALENT_UPERCUT : DELETE_FILES);
    const [save] = useMutation(talentId ? ADD_FILE_FOR_TALENT_UPERCUT : ADD_FILE);

    const processDocumentType = useCallback(async (documentInput: any, documentType: string, currentUploadedFiles: any[]) => {
        const prevData = [...documentInput[documentType]];

        // Check if there's any file to delete
        const filesToDelete = currentUploadedFiles
            ?.filter(({ _id }) => !!_id)
            ?.filter(({ _id }) => !documentInput[documentType]?.find(({ _id: id }) => id === _id));

        if (filesToDelete?.length) {
            await deleteFiles({
                variables: {
                    _ids: filesToDelete.map(({ _id }) => _id),
                    ...(talentId ? { talentId } : {}),
                },
            });
        }

        // Check if there's any file to add
        await Promise.all(
            documentInput[documentType].map(async (file, index) => {
                if (!!file._id) return;
                const uploadResult = await save({
                    variables: {
                        file: file,
                        context: documentType,
                        ...(talentId ? { talentId } : {}),
                    },
                    context: {
                        fetchOptions: {
                            useUpload: true,
                            onProgress: (ev: ProgressEvent) => {
                                const prevData = [...documentInput[documentType]];
                                prevData[index].uploadProgress = Math.round((ev.loaded / ev.total) * 100);
                                setValue(documentType as any, prevData);
                            },
                        },
                    },
                });
                prevData[index] = talentId ? uploadResult.data?.addFileForTalentUpercut : uploadResult.data?.addFile;
            }),
        );

        return prevData;
    }, [deleteFiles, save, setValue]);

    const submitHandler = useCallback(
        async documentInput => {
            setLoading(true);

            try {
                const updatedDocuments: Record<string, any[]> = {};
                const updatedUploadedFiles: Record<string, any[]> = {};

                // Process each document type in the group
                for (const doc of groupedDocument.documents) {
                    const processedData = await processDocumentType(
                        documentInput,
                        doc.type,
                        uploadedFiles[doc.type]
                    );
                    updatedDocuments[doc.type] = processedData;
                    updatedUploadedFiles[doc.type] = processedData;
                }

                // Update uploaded files state
                setUploadedFiles(updatedUploadedFiles);

                onSubmitted?.();
                reset(updatedDocuments);
            } catch (error) {
                console.error('Error uploading documents:', error);
            } finally {
                setLoading(false);
            }
        },
        [processDocumentType, onSubmitted, reset, groupedDocument.documents, uploadedFiles],
    ); return (
        <FormContainer
            style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
            onBack={() => { }}
            loading={loading}
            formState={!isDirty ? undefined : 'edit'}
            onCancel={() => {
                reset(defaultValues);
                onCancel?.();
            }}
            onSubmit={handleSubmit(submitHandler)}
            submitText={props.isEdit ? 'Enregistrer' : 'Suivant'}
            formTitle={groupedDocument.name}
            formDescription={groupedDocument.description}
        >
            <Box sx={{ flexGrow: 1, py: 2 }}>
                <Grid container spacing={3}>
                    {groupedDocument.documents.map((doc, index) => (
                        <Grid item xs={12} md={6} key={doc.type}>
                            <InputDocuments
                                name={doc.type}
                                control={control}
                                label={doc.name}
                                helpText={doc.helpText}
                                placeholder={doc.placeholder || `Glissez déposez votre fichier (pdf, jpg, jpeg, png) pour télécharger ici. (max. 5MB)`}
                                defaultValue=""
                                multiple
                                disabled={loading}
                                files={uploadedFiles[doc.type]}
                                showErrorMessage
                            />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </FormContainer>
    );
};

export default TalentGroupedDocuments;
